import { format } from 'date-fns';

/**
 * Loose shape covering both `Stripe.InvoiceLineItem` (standalone) and the line
 * type embedded in `Stripe.Invoice['lines']['data']`, whose types disagree
 * slightly in the Stripe SDK.
 */
export interface InvoiceLineLike {
  id?: string;
  description?: string | null;
  amount: number;
  period?: { start?: number | null; end?: number | null } | null;
}

export interface LinePeriodGroup {
  key: string;
  start: number;
  end: number;
  lines: InvoiceLineLike[];
}

/** Group invoice line items by their (start, end) billing period for display. */
export function groupLinesByPeriod(inv: { lines?: { data: InvoiceLineLike[] } } | null | undefined): LinePeriodGroup[] {
  if (!inv?.lines?.data) return [];
  const map = new Map<string, LinePeriodGroup>();
  for (const line of inv.lines.data) {
    const start = line.period?.start ?? 0;
    const end = line.period?.end ?? 0;
    const key = `${start}-${end}`;
    const existing = map.get(key);
    if (existing) {
      existing.lines.push(line);
    } else {
      map.set(key, { key, start, end, lines: [line] });
    }
  }
  return [...map.values()].sort((a, b) => a.start - b.start);
}

/** Render a "MMM d–MMM d, yyyy" period header for a line group. */
export function formatPeriodHeader(start: number, end: number): string {
  if (!start || !end) return '';
  const s = new Date(start * 1000);
  const e = new Date(end * 1000);
  const sameYear = s.getFullYear() === e.getFullYear();
  const sPart = format(s, sameYear ? 'MMM d' : 'MMM d, yyyy');
  const ePart = format(e, 'MMM d, yyyy');
  return `${sPart}–${ePart}`.toUpperCase();
}

/**
 * Strip noisy phrases Stripe appends to invoice line descriptions:
 *   - "(with 50% off)"
 *   - " after May 1, 2026"
 *   - leading "1 × "
 *   - trailing "(at $10.00 / month)"
 */
export function simplifyLineDescription(description: string | null | undefined): string {
  if (!description) return '';
  return description
    .replace(/\s*\(with [\d.]+% off\)/, '')
    .replace(/\s+after\s+[A-Za-z]+\s+\d+,?\s*\d{4}\s*$/, '')
    .replace(/^\d+\s*×\s*/, '')
    .replace(/\s*\(at\s.+?\/\s*\w+\)\s*$/, '')
    .trim();
}
